import { isoLangs } from '@zedoc/iso-langs';
import { USERS_GROUP_TYPE__ORGANIZATION } from '../common/constants';

/**
 * TECH-2730 RA10. The languages a staff user may choose for their account
 * emails: the union of `messageLanguages` over the ORGANISATION-type groups
 * given, which is exactly the set the organisation writes its messages in and
 * serves patients from. Not the portal's display languages -- those are the
 * seven UI bundles and have nothing to do with what an organisation has
 * authored.
 *
 * The first option is always "Default (English)", value '' -- the absence of
 * a preference, which the server stores as no field at all.
 */
export const DEFAULT_NOTIFICATION_LANGUAGE = '';

/**
 * The label for one stored value: "French (fr)", or "Default (English)" for
 * none. The ONE rendering of the field, shared by the user dialog, the
 * avatar menu, the Users table (QA round 4, issue 6) and My profile (issue 8).
 */
export const notificationLanguageLabel = (code, t) => {
  if (!code) {
    return t('forms:notificationLanguage.default');
  }
  return isoLangs[code] ? `${isoLangs[code].name} (${code})` : code;
};

/**
 * Options from a list of ISO codes: "Default (English)" first, then each code
 * labelled with its name, sorted. Used directly by the avatar menu, whose
 * codes come from api.currentUser.notificationLanguages.
 */
export const notificationLanguageOptionsFromCodes = (codes, t) => {
  const options = [...new Set(codes || [])].sort().map((code) => ({
    value: code,
    label: notificationLanguageLabel(code, t),
  }));
  return [
    {
      value: DEFAULT_NOTIFICATION_LANGUAGE,
      label: notificationLanguageLabel(DEFAULT_NOTIFICATION_LANGUAGE, t),
    },
    ...options,
  ];
};

export const notificationLanguageOptions = (groups, t) => {
  const codes = [];
  (groups || []).forEach((group) => {
    if (group && group.type === USERS_GROUP_TYPE__ORGANIZATION) {
      codes.push(...(group.messageLanguages || []));
    }
  });
  return notificationLanguageOptionsFromCodes(codes, t);
};

/**
 * The organisations a set of groups sits in, INCLUDING their parents.
 *
 * Every group carries `belongsTo`, a domain path ending in "/". An
 * organisation's `belongsTo` is its own domain; a team's is the domain it
 * lives in; a nested organisation's domain extends its parent's
 * (`Zedoc/Client/` under `Zedoc/`). So the organisations that apply to a
 * group are every organisation-type group whose domain is a prefix of the
 * group's -- the group's own organisation and each one above it. Jubayer,
 * 2026-10-01: a language configured on a parent organisation counts too.
 *
 * @param {string[]} selectedGroupIds group ids chosen in the user dialog
 * @param {object} groupsDb            every group visible to the caller, by id
 * @returns {object[]} organisation groups, deduplicated, in `groupsDb` order
 */
export const organisationsForGroups = (selectedGroupIds, groupsDb) => {
  const db = groupsDb || {};
  const domains = (selectedGroupIds || [])
    .map((id) => db[id] && db[id].belongsTo)
    .filter(Boolean);
  if (!domains.length) {
    return [];
  }
  return Object.values(db).filter(
    (group) =>
      group &&
      group.type === USERS_GROUP_TYPE__ORGANIZATION &&
      typeof group.belongsTo === 'string' &&
      domains.some((domain) => domain.startsWith(group.belongsTo)),
  );
};

/**
 * The options for the user dialog: the languages of the organisations the
 * SELECTED groups sit in, parents included. Nothing selected yet means only
 * the default -- the list cannot be known before the groups are.
 */
export const notificationLanguageOptionsForGroups = (
  selectedGroupIds,
  groupsDb,
  t,
) =>
  notificationLanguageOptions(
    organisationsForGroups(selectedGroupIds, groupsDb),
    t,
  );

export default notificationLanguageOptions;
